import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Card } from '@/components/ui/card'
import type { LoyaltyTier } from '@/types'

/**
 * The Wave 2 growth levers: tiers, stamp expiry, double-stamp windows.
 *
 * Lives in its own file rather than inside AdminLoyaltySettings (already
 * 2,200 lines) and takes the draft slice it owns plus a patch callback, so
 * the parent keeps one save path and this stays reviewable.
 *
 * Every control here is off by default and says, in the copy, what turning it
 * on will actually do to a guest — these are the settings most likely to be
 * switched on without thinking and noticed by customers first.
 */

export interface GrowthDraft {
  tiers: LoyaltyTier[]
  tierWindowEnabled: boolean
  tierWindowDays: string
  stampExpiryEnabled: boolean
  stampExpiryDays: string
  stampExpiryNudgeEnabled: boolean
  stampExpiryNudgeDays: string
  doubleStampEnabled: boolean
  doubleStampWeekdays: number[]
  doubleStampStartHour: string
  doubleStampEndHour: string
  doubleStampMultiplier: string
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MAX_TIERS = 6

/** Slug a label into a stable key. The key is stored on customers.tier and
 *  used in segments, so it must not carry spaces or case. */
function slugify(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24)
}

export function LoyaltyGrowthPanel({
  draft,
  onChange,
  thresholdVisits,
}: {
  draft: GrowthDraft
  onChange: (patch: Partial<GrowthDraft>) => void
  /** The programme's visits-per-reward, used only to explain expiry in terms
   *  the owner already has on screen. */
  thresholdVisits: string
}) {
  const setTier = (i: number, patch: Partial<LoyaltyTier>) => {
    const next = draft.tiers.map((t, n) => (n === i ? { ...t, ...patch } : t))
    onChange({ tiers: next })
  }

  const sortedForPreview = [...draft.tiers]
    .filter((t) => t.min_visits > 0)
    .sort((a, b) => a.min_visits - b.min_visits)

  const expiryDays = Number(draft.stampExpiryDays)
  const nudgeDays = Number(draft.stampExpiryNudgeDays)
  const nudgeTooLate =
    draft.stampExpiryEnabled &&
    draft.stampExpiryNudgeEnabled &&
    Number.isFinite(expiryDays) &&
    Number.isFinite(nudgeDays) &&
    (nudgeDays < 1 || nudgeDays >= expiryDays)

  const toggleDay = (d: number) => {
    const has = draft.doubleStampWeekdays.includes(d)
    onChange({
      doubleStampWeekdays: has
        ? draft.doubleStampWeekdays.filter((x) => x !== d)
        : [...draft.doubleStampWeekdays, d].sort(),
    })
  }

  return (
    <div className="space-y-5">
      {/* ── Tiers ─────────────────────────────────────────────────────── */}
      <Card className="p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-semibold">Tiers</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Rank guests by how often they visit, and give the regulars
              something the rest don't get. Ranked on visits, not spend, so
              this works whether or not you ring sales through Bhookly.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={draft.tiers.length >= MAX_TIERS}
            onClick={() =>
              onChange({
                tiers: [...draft.tiers, { key: '', label: '', min_visits: 0, perk: '' }],
              })
            }
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden /> Add a tier
          </Button>
        </div>

        {draft.tiers.length === 0 ? (
          <p className="mt-3 rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
            No tiers. Guests just collect stamps toward the reward — which is a
            perfectly good programme on its own.
          </p>
        ) : (
          <div className="mt-3 space-y-2">
            {draft.tiers.map((t, i) => (
              <div
                key={i}
                className="grid gap-2 rounded-md border border-border bg-muted/20 p-3 sm:grid-cols-[1.1fr_auto_1.6fr_auto]"
              >
                <div className="space-y-1">
                  <Label htmlFor={`tier-label-${i}`} className="text-xs">
                    Name
                  </Label>
                  <Input
                    id={`tier-label-${i}`}
                    value={t.label}
                    placeholder="Gold"
                    maxLength={40}
                    onChange={(e) => {
                      const label = e.target.value
                      // Keep the key in lockstep with the label while the key
                      // is still auto-derived. Once a tier has been saved its
                      // key is referenced by customers.tier and segments, so
                      // renaming the label must NOT silently re-key it and
                      // strand everyone already in that tier.
                      const autoKeyed = t.key === '' || t.key === slugify(t.label)
                      setTier(i, autoKeyed ? { label, key: slugify(label) } : { label })
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`tier-min-${i}`} className="text-xs">
                    From
                  </Label>
                  <Input
                    id={`tier-min-${i}`}
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={10000}
                    className="w-24"
                    value={t.min_visits || ''}
                    placeholder="10"
                    onChange={(e) => setTier(i, { min_visits: Number(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`tier-perk-${i}`} className="text-xs">
                    What they get
                  </Label>
                  <Input
                    id={`tier-perk-${i}`}
                    value={t.perk}
                    placeholder="Free upgrade on any drink"
                    maxLength={120}
                    onChange={(e) => setTier(i, { perk: e.target.value })}
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onChange({ tiers: draft.tiers.filter((_, n) => n !== i) })}
                    title="Remove this tier"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden />
                    <span className="sr-only">Remove tier</span>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {sortedForPreview.length > 0 && (
          <p className="mt-3 text-sm text-muted-foreground">
            {sortedForPreview
              .map((t, i) => {
                const next = sortedForPreview[i + 1]
                const range = next
                  ? `${t.min_visits}–${next.min_visits - 1} visits`
                  : `${t.min_visits}+ visits`
                return `${t.label || t.key || 'Unnamed'}: ${range}`
              })
              .join(' · ')}
          </p>
        )}

        {draft.tiers.length > 0 && (
          <div className="mt-4 space-y-2 border-t pt-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Label htmlFor="tier-window" className="text-sm font-semibold">
                  Tiers can be lost
                </Label>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  Count only visits from a recent window, so a guest keeps their
                  tier by staying a regular. Off means a tier is earned once and
                  kept forever.
                </p>
              </div>
              <Switch
                id="tier-window"
                checked={draft.tierWindowEnabled}
                onCheckedChange={(v) => onChange({ tierWindowEnabled: v })}
              />
            </div>
            {draft.tierWindowEnabled && (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={3650}
                  className="w-28"
                  value={draft.tierWindowDays}
                  onChange={(e) => onChange({ tierWindowDays: e.target.value })}
                />
                <span className="text-sm text-muted-foreground">
                  days — only visits inside this window count toward a tier.
                </span>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* ── Stamp expiry ──────────────────────────────────────────────── */}
      <Card className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-semibold">Stamps expire</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Give collected stamps a shelf life, so a card that's been sitting
              at {thresholdVisits ? `${Number(thresholdVisits) - 1}/${thresholdVisits}` : 'half-full'} for
              two years doesn't come back to be cashed in. Their visit history
              is unaffected — only progress toward the reward lapses.
            </p>
          </div>
          <Switch
            id="stamp-expiry"
            checked={draft.stampExpiryEnabled}
            onCheckedChange={(v) => onChange({ stampExpiryEnabled: v })}
          />
        </div>

        {draft.stampExpiryEnabled && (
          <div className="mt-3 space-y-3 border-t pt-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm">Stamps last</span>
              <Input
                type="number"
                inputMode="numeric"
                min={7}
                max={3650}
                className="w-28"
                value={draft.stampExpiryDays}
                onChange={(e) => onChange({ stampExpiryDays: e.target.value })}
              />
              <span className="text-sm text-muted-foreground">
                days from the visit that earned them.
              </span>
            </div>

            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Label htmlFor="expiry-nudge" className="text-sm font-semibold">
                  Warn them first
                </Label>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  Message guests before their oldest stamps lapse. Expiring
                  someone's progress silently is a good way to lose them for
                  good.
                </p>
              </div>
              <Switch
                id="expiry-nudge"
                checked={draft.stampExpiryNudgeEnabled}
                onCheckedChange={(v) => onChange({ stampExpiryNudgeEnabled: v })}
              />
            </div>

            {draft.stampExpiryNudgeEnabled && (
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  className="w-24"
                  value={draft.stampExpiryNudgeDays}
                  onChange={(e) => onChange({ stampExpiryNudgeDays: e.target.value })}
                />
                <span className="text-sm text-muted-foreground">
                  days before they expire.
                </span>
                {nudteToLateMessage(nudgeTooLate, expiryDays)}
              </div>
            )}

            <p className="rounded-md bg-amber-50 p-2.5 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              Turning this on applies to stamps guests already hold — anything
              older than the window lapses on the next nightly run, not just
              stamps earned from today.
            </p>
          </div>
        )}
      </Card>

      {/* ── Double stamp ──────────────────────────────────────────────── */}
      <Card className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-semibold">Double-stamp window</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Make each stamp worth more at the times you want busier. The
              classic use is a dead weekday afternoon.
            </p>
          </div>
          <Switch
            id="double-stamp"
            checked={draft.doubleStampEnabled}
            onCheckedChange={(v) => onChange({ doubleStampEnabled: v })}
          />
        </div>

        {draft.doubleStampEnabled && (
          <div className="mt-3 space-y-3 border-t pt-3">
            <div className="space-y-1.5">
              <Label className="text-sm">Days</Label>
              <div className="flex flex-wrap gap-1">
                {DAY_LABELS.map((d, i) => {
                  const on = draft.doubleStampWeekdays.includes(i)
                  return (
                    <button
                      key={d}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleDay(i)}
                      className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
                        on
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {d}
                    </button>
                  )
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Pick none (or all) to run it every day.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm">From</span>
              <Input
                type="number"
                inputMode="numeric"
                min={0}
                max={23}
                className="w-20"
                value={draft.doubleStampStartHour}
                onChange={(e) => onChange({ doubleStampStartHour: e.target.value })}
              />
              <span className="text-sm">until</span>
              <Input
                type="number"
                inputMode="numeric"
                min={0}
                max={23}
                className="w-20"
                value={draft.doubleStampEndHour}
                onChange={(e) => onChange({ doubleStampEndHour: e.target.value })}
              />
              <span className="text-sm text-muted-foreground">
                (24-hour clock; same start and end means all day)
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm">Each stamp counts</span>
              <Input
                type="number"
                inputMode="numeric"
                min={2}
                max={10}
                className="w-20"
                value={draft.doubleStampMultiplier}
                onChange={(e) => onChange({ doubleStampMultiplier: e.target.value })}
              />
              <span className="text-sm text-muted-foreground">times.</span>
            </div>

            <p className="text-sm text-muted-foreground">{doubleStampSummary(draft)}</p>
          </div>
        )}
      </Card>
    </div>
  )
}

function nudteToLateMessage(tooLate: boolean, expiryDays: number) {
  if (!tooLate) return null
  return (
    <span className="text-xs text-destructive">
      Must be between 1 and {Math.max(1, expiryDays - 1)} — a reminder on or
      after the expiry day never gets sent.
    </span>
  )
}

/** Plain-English restatement of the window, so an owner can check what they
 *  set without doing 24-hour-clock arithmetic in their head. */
function doubleStampSummary(d: GrowthDraft): string {
  const mult = Number(d.doubleStampMultiplier)
  const times = mult === 2 ? 'Double' : `${mult}×`
  const days =
    d.doubleStampWeekdays.length === 0 || d.doubleStampWeekdays.length === 7
      ? 'every day'
      : d.doubleStampWeekdays.map((i) => DAY_LABELS[i]).join(', ')
  const start = Number(d.doubleStampStartHour)
  const end = Number(d.doubleStampEndHour)
  const hour = (h: number) => {
    const period = h < 12 ? 'am' : 'pm'
    const display = h % 12 === 0 ? 12 : h % 12
    return `${display}${period}`
  }
  const when = start === end ? 'all day' : `${hour(start)}–${hour(end)}`
  return `${times} stamps ${days}, ${when}.`
}

/**
 * Draft → the slice of LoyaltyConfig the growth levers own.
 *
 * The config PUT replaces the whole blob, so every field here must be sent on
 * every save — omitting one would clear it. "Off" is therefore an explicit
 * `null`, never an absent key.
 *
 * Values are also cleared when their own switch is off rather than left
 * dormant in the blob. A number that springs back to life months later when
 * somebody flips an unrelated toggle is exactly the kind of surprise this
 * settings screen should not be capable of.
 */
export function growthToConfig(d: GrowthDraft) {
  const num = (s: string): number | null => {
    const n = Number(s.trim())
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : null
  }

  // Drop rows the operator added and never filled in, so an empty form row
  // isn't a validation error they have to hunt for.
  const tiers = d.tiers
    .map((t) => ({ ...t, label: t.label.trim(), key: t.key.trim(), perk: t.perk.trim() }))
    .filter((t) => t.key !== '' || t.min_visits > 0)

  const expiryDays = d.stampExpiryEnabled ? num(d.stampExpiryDays) : null

  return {
    tiers,
    tier_window_days: tiers.length > 0 && d.tierWindowEnabled ? num(d.tierWindowDays) : null,
    stamp_expiry_days: expiryDays,
    // A reminder with no expiry to warn about is meaningless, so it only
    // survives while expiry itself is on.
    stamp_expiry_nudge_days:
      expiryDays != null && d.stampExpiryNudgeEnabled ? num(d.stampExpiryNudgeDays) : null,
    double_stamp: d.doubleStampEnabled
      ? {
          enabled: true,
          // All seven days is the same as none; send the canonical "none" so
          // the stored blob doesn't depend on how the owner clicked.
          weekdays: d.doubleStampWeekdays.length === 7 ? [] : [...d.doubleStampWeekdays].sort((a, b) => a - b),
          start_hour: Math.min(23, Math.max(0, Math.floor(Number(d.doubleStampStartHour) || 0))),
          end_hour: Math.min(23, Math.max(0, Math.floor(Number(d.doubleStampEndHour) || 0))),
          multiplier: Math.min(10, Math.max(2, Math.floor(Number(d.doubleStampMultiplier) || 2))),
        }
      : null,
  }
}
